import React, { useState, useCallback, useEffect } from 'react'
import { BellRing } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { clearAuthSession } from '@/lib/authToken'
import { getMe, updateProfile } from '@/features/auth/services/authApi'
import Field from './Field'
import SaveBtn from './SaveBtn'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { usePushNotifications } from '../hooks/usePushNotifications'
import type { LocalUser } from '../types'

type Props = { user: LocalUser | null; workspaceName?: string | undefined }

const ProfileSection: React.FC<Props> = ({ user, workspaceName }) => {
  const { currentTheme: t } = useTheme()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmLogout, setConfirmLogout] = useState(false)
  const push = usePushNotifications()

  const [name, setName] = useState(user?.name ?? '')
  const [jobTitle, setJobTitle] = useState('')
  const [emailSignature, setEmailSignature] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getMe()
      .then((me) => {
        if (cancelled) return
        setName(me.name)
        setJobTitle(me.jobTitle ?? '')
        setEmailSignature(me.emailSignature ?? '')
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const closeDeleteDialog = useCallback(() => setConfirmDelete(false), [])

  const handleLogout = useCallback(() => {
    clearAuthSession()
    window.location.href = '/login'
  }, [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setError(null)
    setSavedMessage(null)
    try {
      const updated = await updateProfile({ name: name.trim(), job_title: jobTitle.trim(), email_signature: emailSignature.trim() })
      try {
        const raw = localStorage.getItem('user')
        const stored = raw ? JSON.parse(raw) : {}
        localStorage.setItem('user', JSON.stringify({ ...stored, name: updated.name }))
      } catch {}
      setSavedMessage('Saved.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [name, jobTitle, emailSignature])

  return (
    <div>
      <div className="flex items-center gap-4 mb-7">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl shrink-0"
          style={{ background: t.accentGradient }}
        >
          {(name || user?.name)?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: t.text }}>{name || user?.name || 'Guest'}</p>
          <p className="text-xs mt-0.5" style={{ color: t.mutedText }}>{user?.email || '—'}</p>
        </div>
      </div>

      <div className="flex flex-col gap-5 mb-4">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Full name" value={name} onChange={setName} placeholder="Your name" />
          <Field label="Job title" value={jobTitle} onChange={setJobTitle} placeholder="e.g. Sales Manager" />
        </div>
        <Field label="Company" value={workspaceName} placeholder="—" readOnly />
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>
            Email signature
          </label>
          <textarea
            value={emailSignature}
            onChange={(e) => setEmailSignature(e.target.value.slice(0, 500))}
            placeholder={'Best,\n' + (name || 'Your name')}
            rows={3}
            className="w-full rounded-[10px] border px-3.5 py-2.5 text-[13.5px] outline-none transition"
            style={{ backgroundColor: t.inputBackground, borderColor: t.border, color: t.text }}
          />
          <p className="text-[11.5px]" style={{ color: t.mutedText }}>
            Zeph appends this when drafting emails on your behalf — this is yours alone, not shared with the rest of the workspace.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-7">
        <SaveBtn fullWidth onClick={() => void handleSave()} disabled={saving} label={saving ? 'Saving…' : 'Save changes'} />
      </div>
      {error && <p className="text-xs mb-7 -mt-4" style={{ color: t.errorText }}>{error}</p>}
      {savedMessage && !error && <p className="text-xs mb-7 -mt-4" style={{ color: t.successText }}>{savedMessage}</p>}

      <div className="rounded-xl p-4 flex flex-col gap-2 mb-7" style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex items-start gap-2.5">
            <BellRing size={16} className="mt-0.5 shrink-0" style={{ color: t.mutedText }} />
            <div>
              <label htmlFor="push-notifications-toggle" className="text-sm font-semibold block" style={{ color: t.text }}>
                Browser notifications
              </label>
              <p className="text-[12.5px] mt-0.5" style={{ color: t.mutedText }}>
                Get notified — like a lead assigned to you, or a qualification result — even when Zeph isn&apos;t open in this browser.
              </p>
            </div>
          </div>
          {push.permission !== 'unsupported' && push.permission !== 'denied' && (
            <Switch
              id="push-notifications-toggle"
              checked={push.subscribed}
              disabled={push.loading || push.busy}
              onCheckedChange={() => void (push.subscribed ? push.disable() : push.enable())}
              aria-label="Browser notifications"
            />
          )}
        </div>
        {push.permission === 'unsupported' && (
          <span className="text-[12px]" style={{ color: t.mutedText }}>Not supported in this browser.</span>
        )}
        {push.permission === 'denied' && (
          <span className="text-[12px]" style={{ color: t.mutedText }}>
            Blocked in your browser&apos;s site settings — enable notifications for this site there, then reload.
          </span>
        )}
        {push.busy && <span className="text-[12px]" style={{ color: t.mutedText }}>Updating&hellip;</span>}
        {push.error && <span className="text-[12px]" style={{ color: t.errorText }}>{push.error}</span>}
      </div>

      <div className="mt-10 rounded-xl border overflow-hidden" style={{ borderColor: t.border }}>
        <p className="text-xs font-semibold uppercase tracking-wide px-5 pt-4 pb-1" style={{ color: t.errorText }}>
          Danger zone
        </p>

        <div className="flex items-center justify-between gap-4 px-5 py-4 border-t" style={{ borderColor: t.border }}>
          <div>
            <p className="text-sm font-medium" style={{ color: t.text }}>Log out</p>
            <p className="text-xs mt-0.5" style={{ color: t.mutedText }}>Sign out of your account on this device.</p>
          </div>
          <button
            onClick={() => setConfirmLogout(true)}
            className="shrink-0 w-20 h-8 rounded-lg text-xs font-semibold transition-colors"
            style={{ border: `1px solid ${t.border}`, color: t.text, backgroundColor: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.hoverBackground }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
          >
            Log out
          </button>
        </div>

        <div className="flex items-center justify-between gap-4 px-5 py-4 border-t" style={{ borderColor: t.border }}>
          <div>
            <p className="text-sm font-medium" style={{ color: t.text }}>Delete account</p>
            <p className="text-xs mt-0.5" style={{ color: t.mutedText }}>Permanently remove your account and all its data.</p>
          </div>
          <button
            onClick={() => setConfirmDelete(true)}
            className="shrink-0 w-20 h-8 rounded-lg text-xs font-semibold transition-colors"
            style={{ border: `1px solid ${t.errorText}`, color: t.errorText, backgroundColor: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.errorBackground }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
          >
            Delete
          </button>
        </div>
      </div>

      <Dialog open={confirmLogout} onOpenChange={setConfirmLogout}>
        <DialogContent
          className="py-6 px-8 rounded-2xl shadow-xl max-w-sm w-full border-none"
          style={{ background: t.secondary, color: t.text }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-center" style={{ color: t.text }}>
              Sign out of Zeph.ai?
            </DialogTitle>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-3 mt-6 flex-row sm:justify-center">
            <Button
              variant="secondary"
              onClick={() => setConfirmLogout(false)}
              className="px-5 py-2 rounded-lg font-medium h-auto text-sm"
              style={{ background: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLogout}
              className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium h-auto text-sm"
            >
              Sign out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent
          className="py-6 px-8 rounded-2xl shadow-xl max-w-sm w-full border-none"
          style={{ background: t.secondary, color: t.text }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-center" style={{ color: t.text }}>
              Account deletion isn't available yet
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-center mt-1" style={{ color: t.mutedText }}>
            Contact support if you need your account removed.
          </p>
          <DialogFooter className="flex justify-center mt-6">
            <Button
              variant="secondary"
              onClick={closeDeleteDialog}
              className="px-5 py-2 rounded-lg font-medium h-auto text-sm"
              style={{ background: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(ProfileSection)
